import { Component, OnInit, Input, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { MoiCommentPermissionModel } from "@models/moi/MoiCommentPermissionModel";
import { EventCommentModel } from "../shared/event-comment-model";
import { EventDataService } from "../services/EventDataService";
import { BsModalService, BsModalRef } from "ngx-bootstrap/modal";
import { EventCommentsDialogComponent } from "./event-comments-dialog/event-comments-dialog.component";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";

@Component({
    selector: "event-comments",
    templateUrl: "./event-comments.component.html",
    styleUrls: ["./event-comments.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("event")],
})
export class EventCommentsComponent implements OnInit {
    private _eventDS = inject(EventDataService);
    private modalService = inject(BsModalService);
    private _promptService = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);

    @Input() eventGuid: string;
    @Input() permissions: MoiCommentPermissionModel = new MoiCommentPermissionModel();

    isLoading = true;
    comments: EventCommentModel[];
    scopedComments: EventCommentModel[];

    showAll = false;

    dialogRef: BsModalRef;

    ngOnInit(): void {
        if (!this.eventGuid) {
            this.isLoading = false;
            return;
        }

        this.loadComments();
    }

    loadComments(): void {
        this._eventDS.getComments(this.eventGuid).subscribe((value) => {
            this.comments = value;
            this.comments = this.comments.sort(
                (val1, val2) => val2.createdAt.getTime() - val1.createdAt.getTime()
            );

            this.showAllClick(false);
            this.isLoading = false;
        });
    }

    newCommentDialog(): Observable<EventCommentModel> {
        const comment = new EventCommentModel();

        return this.openNewDialog(comment);
    }

    newComment() {
        const comment = new EventCommentModel();
        comment.canEdit = this.permissions.canAddComment;

        this.editComment(comment);
    }

    editComment(comment: EventCommentModel) {
        this.openNewDialog(comment).subscribe((editedComment) => {
            if (comment && comment.canEdit) {
                this._eventDS
                    .storeComment(this.eventGuid, editedComment)
                    .subscribe(() => {
                        this.loadComments();
                    });
            }
        });
    }

    private openNewDialog(comment: EventCommentModel): Subject<EventCommentModel> {
        const isExisting = comment && comment.id > 0;

        const config = {
            backdrop: "static",
            class: "comments-dialog",
            initialState: <EventCommentsDialogComponent>{
                title: isExisting
                    ? this._transloco.translate("event.eventComments.editCommentTitle")
                    : this._transloco.translate("event.eventComments.addCommentTitle"),
                eventGuid: this.eventGuid,
                comment: comment,
            },
        };

        this.dialogRef = this.modalService.show(EventCommentsDialogComponent, <any>config);

        return this.dialogRef.content.afterClosed;
    }

    deleteComment(comment: EventCommentModel) {
        this._promptService
            .confirm(
                this._transloco.translate("event.eventComments.deleteCommentTitle"),
                this._transloco.translate("event.eventComments.deleteCommentBody")
            )
            .onConfirm()
            .subscribe(() => {
                this._eventDS.deleteComment(this.eventGuid, comment.id).subscribe(() => {
                    this.loadComments();
                });
            });
    }

    showAllClick(showAll: boolean) {
        if (showAll) {
            this.scopedComments = this.comments;
        } else {
            this.scopedComments = this.comments.slice(0, 5);
        }

        this.showAll = showAll;
    }

    addClick() {
        this.newComment();
    }

    editClick(comment: EventCommentModel) {
        this.editComment(comment);
    }
}
